import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import AuthGuard from '@/components/common/AuthGuard';
import AppShell from '@/components/layout/AppShell';
import HomePage from '@/pages/marketing/HomePage';
import LoginPage from '@/pages/auth/LoginPage';
import SetupPage from '@/pages/auth/SetupPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import PlatformHomePage from '@/pages/PlatformHomePage';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import ReportsPage from '@/pages/dsr/ReportsPage';
import DSRDetailPage from '@/pages/dsr/DSRDetailPage';
import DSRAmendPage from '@/pages/dsr/DSRAmendPage';
import NewDSRPage from '@/pages/dsr/NewDSRPage';
import EditDSRPage from '@/pages/dsr/EditDSRPage';
import AdminPage from '@/pages/AdminPage';
import ProjectSettingsPage from '@/pages/settings/ProjectSettingsPage';
import POBHomePage from '@/pages/pob/POBHomePage';
import POBNewManifestPage from '@/pages/pob/POBNewManifestPage';
import POBManifestPage from '@/pages/pob/POBManifestPage';
import StubPage from '@/pages/StubPage';
import AlertsPage from '@/pages/alerts/AlertsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      retry: 2,
    },
  },
});

function RootRoute() {
  const session = useAuthStore((s) => s.session);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  if (!isInitialized) return null;
  return session ? <Navigate to="/home" replace /> : <HomePage />;
}

function AppRoutes() {
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/setup" element={<SetupPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />

      <Route element={<AuthGuard />}>
        <Route element={<AppShell />}>
          <Route path="/home" element={<PlatformHomePage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/:dsrId" element={<DSRDetailPage />} />
          <Route path="/reports/:dsrId/amend" element={<DSRAmendPage />} />
          <Route path="/dsr/new" element={<NewDSRPage />} />
          <Route path="/dsr/:dsrId/edit" element={<EditDSRPage />} />
          <Route path="/profile" element={<StubPage title="Profile" />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/workforce" element={<StubPage title="Workforce" />} />
          <Route path="/analytics" element={<StubPage title="Analytics" />} />
          <Route path="/settings" element={<StubPage title="Settings" />} />
          <Route path="/settings/projects" element={<ProjectSettingsPage />} />
          <Route path="/pob" element={<POBHomePage />} />
          <Route path="/pob/new" element={<POBNewManifestPage />} />
          <Route path="/pob/:manifestId" element={<POBManifestPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/platform-admin" element={<StubPage title="Platform Admin" />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
